<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'

interface Props {
  min: number
  median: number
  max: number
  height?: number
  width?: number
}
const {
  min,
  median,
  max,
  height = 150,
  width = 300
} = defineProps<Props>()
const chartRef = ref<HTMLCanvasElement | null>(null)
const isVisible = ref(false)
const hasDrawn = ref(false)

// Only draw chart when visible in viewport
useIntersectionObserver(chartRef, ([{ isIntersecting }]) => {
  if (isIntersecting && !hasDrawn.value) {
    isVisible.value = true
    // Use requestIdleCallback to defer drawing off main thread
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        drawChart()
        hasDrawn.value = true
      })
    } else {
      setTimeout(() => {
        drawChart()
        hasDrawn.value = true
      }, 0)
    }
  }
}, { threshold: 0.1 })

const drawChart = () => {
  if (!chartRef.value) return
  const ctx = chartRef.value.getContext('2d')
  if (!ctx) return
  if (chartRef.value.parentElement) {
    const parentWidth = chartRef.value.parentElement.clientWidth
    chartRef.value.width = parentWidth
    chartRef.value.height = height
  }
  const canvasWidth = chartRef.value.width
  const canvasHeight = chartRef.value.height
  ctx.clearRect(0, 0, canvasWidth, canvasHeight)
  const points = [
    { x: 0.0, y: 0.35 },
    { x: 0.08, y: 0.45 },
    { x: 0.16, y: 0.55 },
    { x: 0.24, y: 0.65 },
    { x: 0.32, y: 0.75 },
    { x: 0.4, y: 0.85 },
    { x: 0.45, y: 0.85 },
    { x: 0.5, y: 0.85 },
    { x: 0.6, y: 0.47 },
    { x: 0.7, y: 0.35 },
    { x: 0.8, y: 0.22 },
    { x: 0.9, y: 0.12 },
    { x: 1.0, y: 0.02 }
  ]
  ctx.beginPath()
  ctx.moveTo(0, canvasHeight)
  const startPoint = {
    x: points[0].x * canvasWidth,
    y: canvasHeight - points[0].y * canvasHeight * 0.9
  }
  ctx.lineTo(startPoint.x, startPoint.y)
  for (let i = 0; i < points.length - 3; i += 3) {
    const cp1 = {
      x: points[i + 1].x * canvasWidth,
      y: canvasHeight - points[i + 1].y * canvasHeight * 0.9
    }
    const cp2 = {
      x: points[i + 2].x * canvasWidth,
      y: canvasHeight - points[i + 2].y * canvasHeight * 0.9
    }
    const endPoint = {
      x: points[i + 3].x * canvasWidth,
      y: canvasHeight - points[i + 3].y * canvasHeight * 0.9
    }
    ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, endPoint.x, endPoint.y)
  }
  ctx.lineTo(canvasWidth, canvasHeight)
  ctx.closePath()
  const fillGradient = ctx.createLinearGradient(0, 0, 0, canvasHeight)
  fillGradient.addColorStop(0, 'rgba(79, 70, 229, 0.5)')
  fillGradient.addColorStop(0.3, 'rgba(79, 70, 229, 0.3)')
  fillGradient.addColorStop(0.5, 'rgba(79, 70, 229, 0.1)')
  fillGradient.addColorStop(0.7, 'rgba(79, 70, 229, 0.01)')
  fillGradient.addColorStop(1, 'rgba(79, 70, 229, 0)')
  ctx.fillStyle = fillGradient
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(startPoint.x, startPoint.y)
  for (let i = 0; i < points.length - 3; i += 3) {
    const cp1 = {
      x: points[i + 1].x * canvasWidth,
      y: canvasHeight - points[i + 1].y * canvasHeight * 0.9
    }
    const cp2 = {
      x: points[i + 2].x * canvasWidth,
      y: canvasHeight - points[i + 2].y * canvasHeight * 0.9
    }
    const endPoint = {
      x: points[i + 3].x * canvasWidth,
      y: canvasHeight - points[i + 3].y * canvasHeight * 0.9
    }
    ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, endPoint.x, endPoint.y)
  }
  const strokeGradient = ctx.createLinearGradient(0, 0, canvasWidth, 0)
  strokeGradient.addColorStop(0, 'rgba(79, 70, 229, 0)')
  strokeGradient.addColorStop(0.05, 'rgba(79, 70, 229, 0.5)')
  strokeGradient.addColorStop(0.95, 'rgba(79, 70, 229, 0.5)')
  strokeGradient.addColorStop(1, 'rgba(79, 70, 229, 0)')
  ctx.strokeStyle = strokeGradient
  ctx.lineWidth = 1.5
  ctx.stroke()
  const medianX = canvasWidth * 0.45
  const medianY = canvasHeight - 0.85 * canvasHeight * 0.9
  ctx.beginPath()
  ctx.arc(medianX, medianY, 6, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(79, 70, 229, 1)'
  ctx.fill()
}
const resizeObserver = ref(null)
onMounted(() => {
  // Chart drawing is deferred via useIntersectionObserver
  // Only set up resize observer after first draw
  if (window.ResizeObserver && chartRef.value?.parentElement) {
    resizeObserver.value = new ResizeObserver(() => {
      if (hasDrawn.value) drawChart()
    })
    resizeObserver.value.observe(chartRef.value.parentElement)
  }
  window.addEventListener('resize', () => {
    if (hasDrawn.value) drawChart()
  })
})
onUnmounted(() => {
  window.removeEventListener('resize', drawChart)
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})
watch([() => median, () => min, () => max], drawChart, {
  deep: true
})
</script>

<template>
  <canvas
    ref="chartRef"
    :width="width"
    :height="height"
    class="w-full"
  />
</template>

<style scoped>
canvas {
  width: 100% !important;
  height: auto !important;
  max-height: 180px;
}
</style>
